import { useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/core';
import { BottomButtonsLayout, Button, Headline3, Meta, theme } from 'aimo-ui';
import MenuItem from 'components/common/MenuItem';
import Icon from 'components/icons/Icon';
import { READ_PARKING_SPACE_AVAILABILITY } from 'graphql/queries/readParkingSpaceAvailability';
import useBackButton from 'hooks/useBackButton';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ViewProps } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import Animated, {
  cancelAnimation,
  Easing,
  FadeInRight,
  FadeOutLeft,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { getParkzoneState, useParkzoneStore } from 'stores/parkzoneStore';
import styled from 'styled-components/native';
import {
  CenterColumn,
  ColumnWithMargins,
  StyledWrapper,
} from 'styles/ContainerStyles';
import {
  Query,
  QueryReadPmcAvailabilityArgs,
  ReservedParkingSpace,
} from 'types/generatedSchemaTypes';
import { RootNavProps } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';
import { testIdentifiers } from 'utils/testIdentifiers';

const Container = styled.View`
  flex-grow: 1;
  justify-content: center;
`;

const Row = styled.Pressable`
  flex-direction: row;
  align-items: center;
  gap: 2px;
  margin: 10px 0;
`;

const Refresh = ({ loading }: { loading: boolean }) => {
  const rotate = useSharedValue('0deg');

  const startRotation = () => {
    rotate.value = withRepeat(
      withTiming('360deg', {
        duration: 1000,
        easing: Easing.linear,
      }),
      -1
    );
  };

  const stopRotation = () => {
    cancelAnimation(rotate);
    rotate.value = withSpring('0deg');
  };

  useEffect(() => {
    loading ? startRotation() : stopRotation();
  }, [loading]);

  const rotation = useAnimatedStyle(() => {
    return {
      transform: [
        {
          rotate: rotate.value,
        },
      ],
    };
  });

  return (
    <Animated.View style={rotation}>
      <Icon
        name={'Refresh'}
        size="s"
        color={theme.colors.transparent}
        strokeColor={theme.colors.primary}
      />
    </Animated.View>
  );
};

const enterAnimation = (delay = 500) =>
  isWeb ? undefined : FadeInRight.delay(delay);

const ScrollViewWithHeadline: React.FC<
  ViewProps & { refresh: () => void; loading: boolean }
> = ({ children, refresh, loading }) => {
  const { t } = useTranslation();
  const parkingSpace = useParkzoneStore((state) => state.selectedParkingSpace);
  return (
    <>
      <CenterColumn>
        <Headline3 center {...testIdentifiers('parking-space-headline')}>
          {parkingSpace?.name ?? t('longTermParking.selectParkingSpace')}
        </Headline3>
        <StyledWrapper center mb={0}>
          <Row
            style={({ pressed }) => [{ opacity: pressed || loading ? 0.5 : 1 }]}
            onPress={() => {
              if (!loading) {
                refresh();
              }
            }}>
            <Meta color={theme.colors.primary}>
              {t('longTermParking.refreshList')}
            </Meta>
            <Refresh loading={loading} />
          </Row>
        </StyledWrapper>
      </CenterColumn>
      <ScrollView style={{ opacity: loading ? 0.5 : 1 }} nestedScrollEnabled>
        {children}
      </ScrollView>
    </>
  );
};

const ParkingSpaceSelector: React.FC<{ pmcId: string | undefined }> = ({
  pmcId,
}) => {
  const parkingSpace = useParkzoneStore((state) => state.selectedParkingSpace);
  const { goBack } = useNavigation<RootNavProps>();

  useEffect(() => {
    useParkzoneStore.getState().setSelectedParkingSpace(undefined);
  }, []);

  const handleContinue = () => {
    goBack();
  };

  const onBackPress = () => {
    if (parkingSpace) {
      useParkzoneStore.getState().setSelectedParkingSpace(undefined);
      return true;
    }
    return false;
  };
  useBackButton(onBackPress);

  if (parkingSpace) {
    return (
      <ConfirmParkingSpace
        externalNote={parkingSpace?.externalNote ?? ''}
        handleContinue={handleContinue}
      />
    );
  }
  return <SelectParkingSpace handleContinue={handleContinue} pmcId={pmcId} />;
};

const SelectParkingSpace: React.FC<{
  handleContinue: () => void;
  pmcId: string | undefined;
}> = ({ handleContinue, pmcId }) => {
  const { data, refetch, loading } = useQuery<
    Query,
    QueryReadPmcAvailabilityArgs
  >(READ_PARKING_SPACE_AVAILABILITY, {
    variables: {
      input: {
        pmcId: pmcId as string,
      },
    },
    skip: !pmcId,
    fetchPolicy: 'cache-and-network',
  });
  const parkingSpaces = (data?.readParkingSpaceAvailabilityV2?.content ??
    getParkzoneState().selectedParkingSpaces) as ReservedParkingSpace[];

  const availableParkingSpaces = parkingSpaces
    ?.filter(({ parkingSpaceStatus, parkingSpace }) => {
      return (
        parkingSpaceStatus === 'AVAILABLE' &&
        parkingSpace?.reserved === true &&
        !parkingSpace?.blocked
      );
    })
    .sort((a, b) => {
      return (a.parkingSpace?.name ?? '').localeCompare(
        b.parkingSpace?.name ?? ''
      );
    });

  return (
    <ScrollViewWithHeadline loading={loading} refresh={refetch}>
      <ColumnWithMargins mt={20}>
        {availableParkingSpaces?.map(
          (
            { parkingSpace, pmcIdRef = '' }: ReservedParkingSpace,
            index: number
          ) => {
            const { name, uid = '', externalNote = '' } = parkingSpace || {};
            return (
              <Animated.View
                key={`parking-space-${name}`}
                entering={enterAnimation()}
                exiting={!isWeb ? FadeOutLeft.duration(400) : undefined}>
                <StyledWrapper maxWidth={500} key={uid} mb={10}>
                  <MenuItem
                    testID={`available-parking-space-${index}`}
                    background={theme.colors.gray50}
                    menuLabel={name || ''}
                    subtitle={externalNote ?? ''}
                    withArrow
                    onPress={() => {
                      useParkzoneStore.getState().setSelectedParkingSpace({
                        pmcIdRef: pmcIdRef ?? '',
                        externalNote: externalNote ?? '',
                        name: name ?? '',
                        parkingSpaceUid: uid,
                      });
                      handleContinue();
                    }}
                  />
                </StyledWrapper>
              </Animated.View>
            );
          }
        )}
      </ColumnWithMargins>
    </ScrollViewWithHeadline>
  );
};

const ConfirmParkingSpace: React.FC<{
  handleContinue: () => void;
  externalNote: string;
}> = ({ handleContinue, externalNote }) => {
  const { t } = useTranslation();
  return (
    <BottomButtonsLayout
      scrollable={false}
      contentBottom={
        <StyledWrapper mb={30}>
          <Button
            size="l"
            onPress={handleContinue}
            text={t('longTermParking.selectSpace')}
            testID="confirm-parking-space"
          />
        </StyledWrapper>
      }>
      {externalNote && (
        <Container>
          <Animated.View entering={enterAnimation(300)}>
            <StyledWrapper>
              <Headline3>{t('longTermParking.parkingSpaceFeatures')}</Headline3>
            </StyledWrapper>
            <StyledWrapper>
              <MenuItem
                menuLabel={externalNote}
                noBorder
                background={theme.colors.gray50}
              />
            </StyledWrapper>
          </Animated.View>
        </Container>
      )}
    </BottomButtonsLayout>
  );
};
export default ParkingSpaceSelector;
